import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { updateDb } from "../services/updateDb";
import AlertMessage from "../common/AlertMessage";
import LoadPage from "../common/LoadPage";
import useAxiosFetch from "../common/useAxiosFetch";
import { useAppContext } from "../AppContext";
import moment from "moment";
import { useTranslation } from 'react-i18next';

const ManageEnvironmentPage = () => {
  const { alertMessage, showAlertMessage } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const envId = searchParams.get("envId");

  const { data: environment, loading, refetch } = useAxiosFetch(`/api/environments/${envId}`);
  const { data: plans } = useAxiosFetch("/api/subscription-plans");

  const [formData, setFormData] = useState({
    planId: '',
    subscriptionExpires: '',
    trialExpires: '',
    maxOrganizations: '',
    maxProjects: '',
    maxUsers: '',
    aiCallsPerWeek: '',
    customPrice: ''
  });

  const [enableOverrides, setEnableOverrides] = useState(false);

  useEffect(() => {
    if (environment) {
      const plan = environment.subscriptionPlan || {};
      const overrides = plan.featureOverrides || {};
      
      setFormData({
        planId: plan.planId || '',
        subscriptionExpires: environment.subscriptionExpires 
          ? moment(environment.subscriptionExpires).format('YYYY-MM-DD')
          : '',
        trialExpires: environment.trialExpires && environment.trialExpires > 0
          ? moment(environment.trialExpires).format('YYYY-MM-DD')
          : '',
        maxOrganizations: overrides.maxOrganizations !== undefined ? overrides.maxOrganizations : '',
        maxProjects: overrides.maxProjects !== undefined ? overrides.maxProjects : '',
        maxUsers: overrides.maxUsers !== undefined ? overrides.maxUsers : '',
        aiCallsPerWeek: overrides.aiCallsPerWeek !== undefined ? overrides.aiCallsPerWeek : '',
        customPrice: plan.customPrice || ''
      });

      setEnableOverrides(Object.keys(overrides).length > 0);
    }
  }, [environment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle different field types appropriately
    let processedValue = value;
    if (value !== '') {
      if (name === 'customPrice') {
        processedValue = parseFloat(value);
      } else if (name === 'planId') {
        // Keep as string for plan ID
        processedValue = value;
      } else if (value === '-1') {
        processedValue = -1;
      } else if (name.startsWith('max') || name === 'aiCallsPerWeek') {
        // Parse numbers for feature limits
        processedValue = parseInt(value);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updateData = {
      subscriptionPlan: {
        planId: formData.planId || null,
        customPrice: formData.customPrice || null,
        featureOverrides: enableOverrides ? {
          maxOrganizations: formData.maxOrganizations !== '' ? formData.maxOrganizations : undefined,
          maxProjects: formData.maxProjects !== '' ? formData.maxProjects : undefined,
          maxUsers: formData.maxUsers !== '' ? formData.maxUsers : undefined,
          aiCallsPerWeek: formData.aiCallsPerWeek !== '' ? formData.aiCallsPerWeek : undefined
        } : {}
      },
      subscriptionExpires: formData.subscriptionExpires 
        ? new Date(formData.subscriptionExpires).getTime()
        : null,
      trialExpires: formData.trialExpires
        ? new Date(formData.trialExpires).getTime()
        : (formData.trialExpires === '' ? null : undefined)
    };

    const res = await updateDb(`/api/environments/${envId}`, updateData, "put");

    if (res.success) {
      showAlertMessage('success', t('environment_subscription_updated'));
      refetch();
    } else {
      const errorMsg = typeof res.error === 'string' ? res.error : t('error_updating_environment');
      showAlertMessage('error', errorMsg);
    }
  };

  const getDefaultFeatures = (planId) => {
    const plan = plans?.find(p => p.planId === planId);
    if (!plan) return null;
    return plan.features;
  };

  if (loading) return <LoadPage />;
  if (!environment) return <div>{t('environment_not_found')}</div>;

  const defaultFeatures = formData.planId ? getDefaultFeatures(formData.planId) : null;

  console.log('default features', defaultFeatures);

  return (
    <div id="content">
      <div id="content-title">{t('manage_environment')}: {environment.name}</div>

      <div className="clear-both" />

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble">
        <form onSubmit={handleSubmit}>
          
          {/* Environment Info */}
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ marginTop: 0 }}>{t('environment_information')}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <strong>{t('name')}:</strong> {environment.name}
              </div>
              <div>
                <strong>{t('trial_expires')}:</strong> {environment.trialExpires 
                  ? moment(environment.trialExpires).format('MMM D, YYYY')
                  : 'N/A'}
              </div>
              <div>
                <strong>{t('organizations')}:</strong> {environment.organizations}
              </div>
              <div>
                <strong>{t('domains')}:</strong> {environment.projects}
              </div>
              <div>
                <strong>{t('users')}:</strong> {environment.users}
              </div>
              <div>
                <strong>{t('risks')}:</strong> {environment.risks}
              </div>
            </div>
          </div>

          {/* Subscription Plan */}
          <h4>{t('subscription_configuration')}</h4>
          
          <div className="form-group">
            <label htmlFor="planId">{t('plan')} *</label>
            <select
              id="planId"
              name="planId"
              className="form-control"
              value={formData.planId}
              onChange={handleChange}
              required
            >
              <option value="">{t('select_plan')}</option>
              <option value="trial">Trial</option>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="premium">Premium</option>
              <option value="knmt-starter">KNMT Starter</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="subscriptionExpires">{t('subscription_expiration_date')}</label>
            <input
              type="date"
              id="subscriptionExpires"
              name="subscriptionExpires"
              className="form-control"
              value={formData.subscriptionExpires}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="trialExpires">{t('trial_expiration_date')}</label>
            <input
              type="date"
              id="trialExpires"
              name="trialExpires"
              className="form-control"
              value={formData.trialExpires}
              onChange={handleChange}
            />
            <small className="form-text text-muted">
              {t('leave_empty_no_trial')}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="customPrice">{t('custom_price_eur')} - {t('optional')}</label>
            <input
              type="number"
              step="0.01"
              id="customPrice"
              name="customPrice"
              className="form-control"
              value={formData.customPrice}
              onChange={handleChange}
              placeholder={t('leave_empty_default_price')}
            />
            <small className="form-text text-muted">
              {t('override_default_plan_price')}
            </small>
          </div>

          {/* Feature Overrides */}
          <div style={{ marginTop: '30px', marginBottom: '20px' }}>
            <h4>{t('feature_limits')}</h4>
            <div className="form-check" style={{ marginBottom: '15px' }}>
              <input
                type="checkbox"
                className="form-check-input"
                id="enableOverrides"
                checked={enableOverrides}
                onChange={(e) => setEnableOverrides(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="enableOverrides">
                {t('enable_custom_feature_overrides')}
              </label>
              <small className="form-text text-muted" style={{ display: 'block', marginTop: '5px' }}>
                {t('override_default_plan_features')}
              </small>
            </div>

            {defaultFeatures && (
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#e7f3ff', 
                borderRadius: '5px',
                marginBottom: '15px',
                fontSize: '0.9em'
              }}>
                <strong>{t('default_plan_features')}:</strong>
                <div style={{ marginTop: '5px' }}>
                  • {t('organizations')}: {defaultFeatures.maxOrganizations === -1 ? t('Unlimited') : defaultFeatures.maxOrganizations}<br/>
                  • {t('domains')}: {defaultFeatures.baseProjects !== undefined 
                    ? `${defaultFeatures.baseProjects} ${t('included')}, ${t('unlimited')} ${t('available')}` 
                    : (defaultFeatures.maxProjects === -1 ? t('Unlimited') : defaultFeatures.maxProjects)}<br/>
                  • {t('users')}: {defaultFeatures.maxUsers === -1 ? t('Unlimited') : defaultFeatures.maxUsers}<br/>
                  • {t('ai_per_week')}: {defaultFeatures.aiCallsPerWeek === -1 ? t('Unlimited') : defaultFeatures.aiCallsPerWeek}
                </div>
              </div>
            )}

            <div style={{ opacity: enableOverrides ? 1 : 0.5, pointerEvents: enableOverrides ? 'auto' : 'none' }}>
              <div className="form-group">
                <label htmlFor="maxOrganizations">{t('max_organizations')}</label>
                <input
                  type="number"
                  id="maxOrganizations"
                  name="maxOrganizations"
                  className="form-control"
                  value={formData.maxOrganizations}
                  onChange={handleChange}
                  placeholder={t('leave_empty_default_unlimited')}
                  disabled={!enableOverrides}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxProjects">{t('max_domains')}</label>
                <input
                  type="number"
                  id="maxProjects"
                  name="maxProjects"
                  className="form-control"
                  value={formData.maxProjects}
                  onChange={handleChange}
                  placeholder={t('leave_empty_default_unlimited')}
                  disabled={!enableOverrides}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxUsers">{t('max_users')}</label>
                <input
                  type="number"
                  id="maxUsers"
                  name="maxUsers"
                  className="form-control"
                  value={formData.maxUsers}
                  onChange={handleChange}
                  placeholder={t('leave_empty_default_unlimited')}
                  disabled={!enableOverrides}
                />
              </div>

              <div className="form-group">
                <label htmlFor="aiCallsPerWeek">{t('ai_per_week')}</label>
                <input
                  type="number"
                  id="aiCallsPerWeek"
                  name="aiCallsPerWeek"
                  className="form-control"
                  value={formData.aiCallsPerWeek}
                  onChange={handleChange}
                  placeholder={t('leave_empty_default_unlimited')}
                  disabled={!enableOverrides}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-group" style={{ marginTop: '30px' }}>
            <button type="submit" className="white-border-btn submit-btn">
              <i className="icon-save" style={{ marginRight: '8px' }} />
              {t('save_changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageEnvironmentPage;
